import React, { useContext, useRef } from 'react';

import { mdiViewDashboardOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import slugify from 'slugify';
import styled from 'styled-components';

import { DashboardContext } from '../../context/DashboardContext';
import { prepareDataForCsv } from '../../utils/dashboardCsvExport';
import AddSessions from '../addSessions/AddSessions';
import { useDashboardData } from '../dashboard/useDashboardData';

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    color: white;
    background-color: ${(props) => props.theme.colors.primary.main};
    height: 60px;
    padding: 0.5rem 1rem;
`;

const HeaderIcon = styled.div`
    flex: 0 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.white};

    svg {
        color: ${(props) => props.theme.colors.primary.main};
        max-width: 25px;
        margin-bottom: 1px;
    }
`;

const HeaderTitle = styled.h1`
    flex-shrink: 1;
    margin-left: 1rem;
    font-weight: 300;
    font-size: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const HeaderControls = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
`;

const StyledButton = styled(Button)`
    white-space: nowrap;
    margin-left: 1rem;
`;

const StyledDownloadLink = styled.a`
    display: none;
`;

export interface NavControl {
    label: string;
    onClick: () => void;
}

export interface MainLayoutProps {
    headerControls?: NavControl[];
}

const DashboardLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const { t } = useTranslation('dashboard');
    const { classInfo, classUuid, fetchSessions, view, setView, schoolSettings, isLoading, sessions, selectedSessionUuid } = useContext(DashboardContext);
    const { studentMetricsRows, sessionSummary } = useDashboardData();
    const csvLink = useRef<HTMLAnchorElement>(null);

    const handleDownloadClick = () => {
        prepareDataForCsv(view, { studentMetricsRows, sessionSummary }, schoolSettings, t);
        csvLink?.current?.click();
    };
    const handleStudentViewClick = () => {
        setView((currentView) => (currentView === 'SessionSummary' ? 'StudentDetail' : 'SessionSummary'));
    };

    const downloadCsvUrl = (): string => {
        const data = prepareDataForCsv(view, { studentMetricsRows, sessionSummary }, schoolSettings, t);
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });
        return URL.createObjectURL(blob);
    };

    const generateFileName = () => {
        if (isLoading) return '';

        const currentClassName = slugify(classInfo.class_name, { replacement: '_' });
        const currentSession = sessions.find((session) => session.session_id === selectedSessionUuid);

        switch (view) {
            case 'SessionSummary': {
                // wait for state to finish updating to get current session
                if (currentSession !== undefined) {
                    const currentSessionDate = format(new Date(currentSession.start_time), 'MM_dd_yy');
                    return `${currentClassName}-${currentSessionDate}-${t('dashboard-aggregate')}.csv`;
                }
                break;
            }
            case 'StudentDetail': {
                // wait for state to finish updating to get current session
                if (currentSession !== undefined) {
                    const currentSessionDate = format(new Date(currentSession.start_time), 'MM_dd_yy');
                    return `${currentClassName}-${currentSessionDate}-${t('dashboard-participant-detail')}.csv`;
                }
                break;
            }
            default:
                break;
        }

        return '';
    };

    const getViewTranslationId = (): string => (view === 'SessionSummary' ? 'view-user-details' : 'view-class-details');

    return (
        <Container>
            <StyledDownloadLink download={generateFileName()} target="_self" href={downloadCsvUrl()} ref={csvLink} />
            <Header>
                <HeaderIcon>
                    <Icon path={mdiViewDashboardOutline} />
                </HeaderIcon>
                <HeaderTitle>{t('dashboard')}</HeaderTitle>
                <HeaderControls>
                    <StyledButton id="dashboard-header-download-btn" variant="outlined" color="inherit" size="small" onClick={handleDownloadClick} disabled={isLoading || sessions.length < 1}>
                        {t('generic:Download')}
                    </StyledButton>
                    <StyledButton id={`dashboard-header-${getViewTranslationId()}-btn`} variant="outlined" color="inherit" size="small" onClick={handleStudentViewClick} disabled={isLoading || sessions.length < 1}>
                        {t(getViewTranslationId())}
                    </StyledButton>
                    <AddSessions classInfo={classInfo} classUuid={classUuid} fetchSessions={fetchSessions} />
                </HeaderControls>
            </Header>
            {children}
        </Container>
    );
};

export default DashboardLayout;
